import { useEffect, useMemo, useState } from "react";
import {
  REPORT_TABS, GROUPINGS, DEFAULT_HIDDEN, columnChoices, buildReportTable, roundingNotes, reportCsv, reportSubtitle, REPORT_DISCLAIMER,
} from "../../utils/takeoffReport";
import { useUnitsPreference } from "../../utils/useUnits";

// The printable Takeoff Report: quantities by condition, by drawing or by room, in the viewer's display units, with
// the columns and grouping the person chooses, a plain note of any rounding applied, and a print view (browser Print /
// Save as PDF — same approach as the Design Schedules). Built on the same tables as the CSV export, so every number
// reconciles with the canvas. No waste anywhere: takeoff carries none.

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  .takeoff-report-print, .takeoff-report-print * { visibility: visible !important; }
  .takeoff-report-print { position: absolute !important; left: 0; top: 0; width: 100%; height: auto !important; max-height: none !important; overflow: visible !important; padding: 0 !important; box-shadow: none !important; border: none !important; }
  .takeoff-report-print .no-print { display: none !important; }
  .takeoff-report-print table { font-size: 10px; }
  .takeoff-report-print th, .takeoff-report-print td { padding: 2px 5px !important; }
  .takeoff-report-print .report-group { page-break-inside: avoid; }
}
`;

const pill = (on) => ({ padding: "4px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 999, background: on ? "var(--cs-blue)" : "var(--surface)", color: on ? "#fff" : "var(--text2)", border: `1px solid ${on ? "var(--cs-blue)" : "var(--cs-border)"}` });
const btn = { height: 30, padding: "0 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", background: "var(--surface)", color: "var(--text2)" };
const th = { textAlign: "left", padding: "5px 8px", fontSize: 11, fontWeight: 700, color: "var(--text3)", borderBottom: "2px solid var(--cs-border)", whiteSpace: "nowrap" };
const td = { padding: "4px 8px", fontSize: 12.5, borderBottom: "1px solid var(--cs-border)", verticalAlign: "top" };
const NUMERIC = new Set(["qty", "qty2", "qty3", "instances", "lengthFt", "widthFt", "heightFt", "floorSf"]);

const fmtCell = (key, v) => {
  if (v == null || v === "") return "";
  if (NUMERIC.has(key) && typeof v === "number") return v.toLocaleString(undefined, { maximumFractionDigits: 3 });
  return String(v);
};

function downloadCsv(filename, csv) {
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const HIDDEN_KEY = "cs.takeoff.reportColumns";
const readHidden = () => { try { const v = JSON.parse(localStorage.getItem(HIDDEN_KEY) || "null"); return v && typeof v === "object" ? v : {}; } catch { return {}; } };
const writeHidden = (v) => { try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(v)); } catch { /* not remembered */ } };

export default function TakeoffReport({ rooms, conditions, sheets, project = "", revision = "", unreviewedText = "", onClose }) {
  const [tab, setTab] = useState("condition");
  const [units, , toggleUnits] = useUnitsPreference();
  const [groupByTab, setGroupByTab] = useState({});
  const [hiddenByTab, setHiddenByTab] = useState(() => readHidden());
  const [colsOpen, setColsOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const hidden = hiddenByTab[tab] ?? DEFAULT_HIDDEN[tab];
  const group = groupByTab[tab] ?? GROUPINGS[tab][0].id;
  const data = useMemo(() => ({ rooms, conditions, sheets, revision, units }), [rooms, conditions, sheets, revision, units]);
  const table = useMemo(() => buildReportTable(tab, data, { group, hidden }), [tab, data, group, hidden]);
  const choices = useMemo(() => columnChoices(tab, data, hidden), [tab, data, hidden]);
  const notes = useMemo(() => roundingNotes(conditions, units), [conditions, units]);
  const drawingCount = useMemo(() => new Set(conditions.map(c => c.SheetID).filter(v => v != null)).size, [conditions]);
  const conditionCount = useMemo(() => buildReportTable("condition", data, { group: "none" }).rowCount, [data]);

  const toggleColumn = (key) => {
    const next = { ...hiddenByTab, [tab]: choices.filter(c => (c.key === key ? c.shown : !c.shown)).map(c => c.key) };
    setHiddenByTab(next); writeHidden(next);
  };
  const today = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const empty = table.rowCount === 0;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(15,23,42,0.5)", display: "flex", justifyContent: "center", alignItems: "stretch", padding: "2vh 2vw" }}>
      <style>{PRINT_CSS}</style>
      <div className="takeoff-report-print" role="dialog" aria-label="Takeoff report"
        style={{ width: "min(1100px, 100%)", background: "var(--surface)", borderRadius: 10, boxShadow: "0 16px 48px rgba(0,0,0,0.4)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div className="no-print" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "10px 14px", borderBottom: "1px solid var(--cs-border)" }}>
          {REPORT_TABS.map(t => <button key={t.id} onClick={() => setTab(t.id)} style={pill(tab === t.id)} aria-pressed={tab === t.id}>{t.label}</button>)}
          <span style={{ width: 1, height: 20, background: "var(--cs-border)" }} />
          <label style={{ fontSize: 12, color: "var(--text3)", display: "flex", alignItems: "center", gap: 4 }}>
            Group
            <select value={group} onChange={e => setGroupByTab(g => ({ ...g, [tab]: e.target.value }))} aria-label="Grouping" style={{ fontSize: 12, padding: "3px 4px" }}>
              {GROUPINGS[tab].map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
            </select>
          </label>
          <span style={{ position: "relative" }}>
            <button onClick={() => setColsOpen(o => !o)} style={btn} aria-expanded={colsOpen} aria-haspopup="true">Columns ▾</button>
            {colsOpen && (
              <div role="menu" data-testid="columns-menu" style={{ position: "absolute", top: 34, left: 0, zIndex: 5, minWidth: 190, background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,0.2)", padding: 6 }}>
                {choices.map(c => (
                  <label key={c.key} style={{ display: "flex", gap: 8, alignItems: "center", padding: "3px 6px", fontSize: 12.5, cursor: "pointer" }}>
                    <input type="checkbox" checked={c.shown} onChange={() => toggleColumn(c.key)} /> {c.label}
                  </label>
                ))}
              </div>
            )}
          </span>
          <button onClick={toggleUnits} style={btn} title="Display units — feet / inches or metric. Everything is stored in feet.">{units === "metric" ? "m · m²" : "ft · in"}</button>
          <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button onClick={() => { const out = reportCsv(tab, data, { hidden, project, revision }); downloadCsv(out.filename, out.csv); }} disabled={empty} style={btn} title="Download this table as CSV (opens in Excel)">⬇ CSV</button>
            <button onClick={() => window.print()} disabled={empty} style={{ ...btn, background: "var(--cs-blue)", color: "#fff", borderColor: "var(--cs-blue)" }}>🖨 Print / PDF</button>
            <button onClick={onClose} style={btn} aria-label="Close report">✕</button>
          </span>
        </div>

        <div style={{ overflowY: "auto", padding: "14px 18px", flex: 1 }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>Takeoff report — {REPORT_TABS.find(t => t.id === tab).label.toLowerCase()}</div>
            <div style={{ fontSize: 12.5, color: "var(--text2)" }}>{[project, revision, today].filter(Boolean).join(" · ")}</div>
            <div style={{ fontSize: 12, color: "var(--text3)" }}>{reportSubtitle({ units, drawingCount, conditionCount })}</div>
          </div>
          {unreviewedText && (
            <div role="note" style={{ margin: "0 0 10px", padding: "6px 10px", border: "1px solid #b45309", borderRadius: 6, background: "rgba(217,119,6,0.1)", color: "#92400e", fontSize: 12, fontWeight: 600 }}>
              Includes {unreviewedText} — still priced, but nobody has reviewed them yet.
            </div>
          )}
          {empty ? (
            <div style={{ padding: 24, color: "var(--text3)", fontSize: 13 }}>Nothing taken off yet — draw or place some conditions first.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>{table.columns.map(c => <th key={c.key} style={{ ...th, textAlign: NUMERIC.has(c.key) ? "right" : "left" }}>{c.label}</th>)}</tr>
              </thead>
              {table.groups.map(g => (
                <tbody key={g.key} className="report-group">
                  {table.grouped && (
                    <tr>
                      <td colSpan={table.columns.length} style={{ padding: "8px 8px 3px", fontSize: 12.5, fontWeight: 800, background: "var(--surface2, transparent)", borderBottom: "1px solid var(--cs-border)" }}>
                        {g.label}{g.sub && <span style={{ fontWeight: 500, color: "var(--text3)" }}> · {g.sub}</span>}
                      </td>
                    </tr>
                  )}
                  {g.rows.map((r, i) => (
                    <tr key={i}>{table.columns.map(c => <td key={c.key} style={{ ...td, textAlign: NUMERIC.has(c.key) ? "right" : "left", fontFamily: NUMERIC.has(c.key) ? "ui-monospace, Menlo, Consolas, monospace" : "inherit" }}>{fmtCell(c.key, r[c.key])}</td>)}</tr>
                  ))}
                  {table.grouped && g.subtotals.length > 0 && (
                    <tr>
                      <td colSpan={table.columns.length} style={{ ...td, textAlign: "right", fontWeight: 700, color: "var(--text2)" }}>
                        Subtotal: {g.subtotals.map(s => `${s.qty.toLocaleString(undefined, { maximumFractionDigits: 3 })} ${s.uom}`).join(" · ")}
                      </td>
                    </tr>
                  )}
                </tbody>
              ))}
            </table>
          )}
          {notes.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text3)" }}>Rounding applied</div>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 12, color: "var(--text2)" }}>{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
            </div>
          )}
          <div style={{ marginTop: 14, paddingTop: 8, borderTop: "1px solid var(--cs-border)", fontSize: 11, color: "var(--text3)" }}>{REPORT_DISCLAIMER}</div>
        </div>
      </div>
    </div>
  );
}
